/* =============================================================================
 * M.M.LaFleur — PDP image zoom.
 *
 * Click a gallery still to open it full-bleed; click again to magnify to 2.5x
 * and drag to pan. Arrow keys step between frames, Escape closes.
 *
 * Two things shape the implementation:
 *
 *   1. The theme runs Barba page transitions, so the gallery is replaced on
 *      soft navigation. Clicks are therefore delegated from the document, and
 *      a MutationObserver re-decorates galleries as they appear rather than
 *      binding once on load.
 *
 *   2. The gallery's srcset tops out at 1200w, which is a thumbnail once it
 *      fills the viewport. Shopify's CDN takes a `width` query parameter, so
 *      the opener upgrades the largest candidate to 2048 and swaps it in when
 *      it arrives — the thumbnail shows immediately so the click feels instant.
 * ========================================================================== */

(function () {
  "use strict";

  var MAX_SCALE = 2.5;
  var HI_RES = 2048;

  var overlay = null;
  var stageImg = null;
  var countNow = null;
  var countAll = null;

  var frames = [];
  var index = 0;
  var scale = 1;
  var originX = 50;
  var originY = 50;
  var opener = null;
  var pointerId = null;
  var panStart = null;

  /* Full-size files already requested, keyed by URL. Holding the Image objects
     keeps them from being collected, so a neighbour fetched ahead of time is
     decoded and ready the instant you step onto it. */
  var cache = Object.create(null);

  /* --- helpers ---------------------------------------------------------- */

  function largestCandidate(img) {
    var set = img.getAttribute("srcset");
    if (!set) return img.currentSrc || img.src;
    var best = null;
    var bestW = -1;
    set.split(",").forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      if (!bits[0]) return;
      var w = parseInt(bits[1] || "0", 10) || 0;
      if (w > bestW) { bestW = w; best = bits[0]; }
    });
    return best || img.currentSrc || img.src;
  }

  function hiRes(img) {
    var from = largestCandidate(img);
    try {
      var u = new URL(from, window.location.href);
      if (u.searchParams.has("width")) {
        u.searchParams.set("width", String(HI_RES));
        return u.href;
      }
      return from;
    } catch (err) {
      return from;
    }
  }

  function preload(url) {
    if (!url) return null;
    if (cache[url]) return cache[url];
    var im = new Image();
    cache[url] = im;
    im.src = url;
    return im;
  }

  function isReady(url) {
    var im = cache[url];
    return !!(im && im.complete && im.naturalWidth > 0);
  }

  /* Fetch the frames either side of the current one. Stepping through a
     gallery is overwhelmingly a next/previous action, so by the time the
     arrow is pressed the file is usually already decoded and the swap is
     instant rather than a visible load. */
  function preloadNeighbours() {
    if (frames.length < 2) return;
    [1, -1].forEach(function (delta) {
      var frame = frames[(index + delta + frames.length) % frames.length];
      if (frame) preload(hiRes(frame));
    });
  }

  /* Only stills. Video tiles keep their own controls, and a tile with no <img>
     has nothing to open. */
  function zoomableImages(root) {
    var out = [];
    (root || document).querySelectorAll(".mm-gallery .mm-gallery-item").forEach(function (item) {
      if (item.querySelector("video")) return;
      var img = item.querySelector("img");
      if (img) out.push(img);
    });
    return out;
  }

  function decorate() {
    zoomableImages(document).forEach(function (img) {
      if (img.hasAttribute("data-mm-zoomable")) return;
      img.setAttribute("data-mm-zoomable", "");
      img.setAttribute("tabindex", "0");
      img.setAttribute("role", "button");
      var label = img.getAttribute("alt") || "product image";
      img.setAttribute("aria-label", "Zoom " + label);
    });
  }

  /* --- overlay ---------------------------------------------------------- */

  function icon(paths) {
    return '<svg class="mm-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" ' +
      'stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">' + paths + "</svg>";
  }

  function build() {
    if (overlay) return;
    overlay = document.createElement("div");
    overlay.className = "mm-zoom";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Product image viewer");
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="mm-zoom-scrim" data-mm-zoom-close></div>' +
      '<button type="button" class="mm-zoom-close" data-mm-zoom-close aria-label="Close">' +
      icon('<path d="M18 6 6 18"/><path d="m6 6 12 12"/>') + "</button>" +
      '<button type="button" class="mm-zoom-prev" aria-label="Previous image">' +
      icon('<path d="m15 18-6-6 6-6"/>') + "</button>" +
      '<button type="button" class="mm-zoom-next" aria-label="Next image">' +
      icon('<path d="m9 18 6-6-6-6"/>') + "</button>" +
      '<figure class="mm-zoom-stage"><img alt=""></figure>' +
      '<p class="mm-zoom-count"><span data-now>1</span> / <span data-all>1</span></p>';

    document.body.appendChild(overlay);
    stageImg = overlay.querySelector(".mm-zoom-stage img");
    countNow = overlay.querySelector("[data-now]");
    countAll = overlay.querySelector("[data-all]");

    /* Bound straight to the elements. The previous version delegated from the
       overlay and resolved the target with closest(), which depended on the
       click landing somewhere that could walk back up to the button — fragile
       when the target is an SVG child. Paired with pointer-events: none on the
       icons in mm-zoom.css, a single click on the glyph now always closes. */
    overlay.querySelector(".mm-zoom-scrim").addEventListener("click", close);
    overlay.querySelector(".mm-zoom-close").addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      close();
    });
    overlay.querySelector(".mm-zoom-prev").addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      step(-1);
    });
    overlay.querySelector(".mm-zoom-next").addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      step(1);
    });

    stageImg.addEventListener("click", function (e) {
      /* A drag ends with a click event; ignore it so panning doesn't collapse
         the zoom the moment you let go. */
      if (panStart && panStart.moved) return;
      toggleZoom(e);
    });

    stageImg.addEventListener("pointerdown", onPointerDown);
    stageImg.addEventListener("pointermove", onPointerMove);
    stageImg.addEventListener("pointerup", onPointerUp);
    stageImg.addEventListener("pointercancel", onPointerUp);
  }

  function render() {
    var img = frames[index];
    if (!img) return;

    resetZoom();
    overlay.setAttribute("data-single", frames.length < 2 ? "true" : "false");
    countNow.textContent = String(index + 1);
    countAll.textContent = String(frames.length);
    stageImg.alt = img.getAttribute("alt") || "";

    var full = hiRes(img);
    var wanted = index;

    /* Already fetched — usually the case when stepping, because the neighbour
       was pulled down while the previous frame was on screen. Go straight to
       full resolution with no preview stage at all. */
    if (isReady(full)) {
      stageImg.src = full;
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
      return;
    }

    /* Otherwise show whatever the gallery already has so the frame is never
       blank, and swap when the full file lands. */
    stageImg.src = img.currentSrc || img.src;
    if (full === stageImg.src) {
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
      return;
    }

    overlay.setAttribute("data-loading", "true");
    var pending = preload(full);
    var settle = function () {
      if (wanted !== index || !overlay || overlay.hidden) return;
      stageImg.src = full;
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
    };
    if (pending.complete && pending.naturalWidth > 0) { settle(); return; }
    pending.addEventListener("load", settle, { once: true });
    pending.addEventListener("error", function () {
      overlay.setAttribute("data-loading", "false");
      preloadNeighbours();
    }, { once: true });
  }

  function applyTransform() {
    stageImg.style.transformOrigin = originX + "% " + originY + "%";
    stageImg.style.transform = "scale(" + scale + ")";
  }

  function resetZoom() {
    scale = 1;
    originX = 50;
    originY = 50;
    overlay.setAttribute("data-zoomed", "false");
    applyTransform();
  }

  function toggleZoom(e) {
    if (scale > 1) { resetZoom(); return; }
    var box = stageImg.getBoundingClientRect();
    originX = ((e.clientX - box.left) / box.width) * 100;
    originY = ((e.clientY - box.top) / box.height) * 100;
    scale = MAX_SCALE;
    overlay.setAttribute("data-zoomed", "true");
    applyTransform();
  }

  function onPointerDown(e) {
    if (scale === 1) return;
    pointerId = e.pointerId;
    panStart = { x: e.clientX, y: e.clientY, ox: originX, oy: originY, moved: false };
    overlay.setAttribute("data-panning", "true");
    stageImg.setPointerCapture(pointerId);
  }

  function onPointerMove(e) {
    if (!panStart || e.pointerId !== pointerId) return;
    var box = stageImg.getBoundingClientRect();
    var dx = e.clientX - panStart.x;
    var dy = e.clientY - panStart.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) panStart.moved = true;
    /* Dragging right should reveal what's off the left edge, so the origin
       moves against the pointer. */
    originX = Math.min(100, Math.max(0, panStart.ox - (dx / box.width) * 100));
    originY = Math.min(100, Math.max(0, panStart.oy - (dy / box.height) * 100));
    applyTransform();
  }

  function onPointerUp(e) {
    if (!panStart || e.pointerId !== pointerId) return;
    overlay.setAttribute("data-panning", "false");
    if (stageImg.hasPointerCapture(pointerId)) stageImg.releasePointerCapture(pointerId);
    pointerId = null;
    /* Cleared after the click event has had its chance to fire. */
    window.setTimeout(function () { panStart = null; }, 0);
  }

  function step(delta) {
    if (frames.length < 2) return;
    index = (index + delta + frames.length) % frames.length;
    render();
  }

  function open(img) {
    build();
    frames = zoomableImages(document);
    index = frames.indexOf(img);
    if (index < 0) { frames = [img]; index = 0; }

    opener = img;
    overlay.hidden = false;
    /* Next frame, so the opacity transition has a starting value to animate
       from rather than appearing fully formed. */
    window.requestAnimationFrame(function () {
      overlay.setAttribute("data-open", "true");
    });
    document.body.classList.add("mm-no-scroll");
    render();
    preloadNeighbours();
    overlay.querySelector(".mm-zoom-close").focus();
  }

  function close() {
    if (!overlay || overlay.hidden) return;
    overlay.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
    window.setTimeout(function () {
      if (overlay.getAttribute("data-open") === "false") overlay.hidden = true;
    }, 240);
    if (opener && document.contains(opener)) opener.focus();
    opener = null;
  }

  function isOpen() {
    return overlay && !overlay.hidden && overlay.getAttribute("data-open") === "true";
  }

  /* --- wiring ----------------------------------------------------------- */

  document.addEventListener("click", function (e) {
    var img = e.target.closest(".mm-gallery .mm-gallery-item img");
    if (!img) return;
    if (e.target.closest(".mm-shop-look-btn")) return;
    if (img.closest(".mm-gallery-item").querySelector("video")) return;
    e.preventDefault();
    open(img);
  });

  document.addEventListener("keydown", function (e) {
    /* Enter/Space on a focused gallery still. */
    var focused = document.activeElement;
    if (!isOpen() && focused && focused.matches && focused.matches("img[data-mm-zoomable]")) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(focused); }
      return;
    }
    if (!isOpen()) return;

    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    else if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    else if (e.key === "Tab") {
      /* Keep focus inside the dialog. Two gaps in the earlier version let Tab
         escape to the page behind: it only wrapped when focus was exactly on
         the first or last button, so focus on the overlay or the stage walked
         straight out; and if the list came up empty it returned without
         preventing the default. idx -1 now wraps, and an empty list holds
         focus where it is. The visibility test drops the prev/next buttons that
         mm-zoom.css hides on a single frame or while zoomed. */
      var stops = Array.prototype.filter.call(
        overlay.querySelectorAll("button:not([disabled])"),
        function (b) {
          return b.getClientRects().length > 0 && window.getComputedStyle(b).visibility !== "hidden";
        }
      );
      if (!stops.length) { e.preventDefault(); return; }
      var idx = stops.indexOf(document.activeElement);
      if (e.shiftKey && idx <= 0) { e.preventDefault(); stops[stops.length - 1].focus(); }
      else if (!e.shiftKey && (idx === -1 || idx === stops.length - 1)) { e.preventDefault(); stops[0].focus(); }
    }
  });

  function init() {
    decorate();
    /* Barba swaps the main container on navigation, so watch for new galleries
       instead of assuming this page's DOM is final. */
    var observer = new MutationObserver(function () { decorate(); });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}());
